<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import type { ComboboxContentEmits, ComboboxContentProps } from 'reka-ui'
import { reactiveOmit } from '@vueuse/core'
import {
  ComboboxContent,
  ComboboxPortal,
  ComboboxViewport,
  useForwardPropsEmits,
} from 'reka-ui'
import { cn } from '@ds/lib/utils'

/*
 * Styled to match DSSelectContent, so the two dropdowns are one thing on
 * screen; `--reka-combobox-trigger-width` keeps the panel the width of the
 * field rather than the registry's fixed 200px.
 */
const props = withDefaults(
  defineProps<ComboboxContentProps & { class?: HTMLAttributes['class'] }>(),
  {
    position: 'popper',
    align: 'start',
    sideOffset: 4,
  },
)
const emits = defineEmits<ComboboxContentEmits>()

const delegatedProps = reactiveOmit(props, 'class')

const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <ComboboxPortal>
    <ComboboxContent
      v-bind="forwarded"
      :class="cn(
        'relative z-50 max-h-96 overflow-hidden font-ds',
        'w-(--reka-combobox-trigger-width) min-w-32',
        'bg-popover text-popover-foreground border border-border rounded-ds-md shadow-ds-lg',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        props.class,
      )"
    >
      <ComboboxViewport class="divide-y divide-border/50 rounded-ds-none">
        <slot />
      </ComboboxViewport>
    </ComboboxContent>
  </ComboboxPortal>
</template>
